import { useEffect, useState } from "react";
import type { Comment } from "../../api/comments/comments";
import {
	getLinkedinJobPostingsByPersonId,
	LinkedinJobPosting,
} from "../../api/job/linkedin_job_posting";
import {
	getLLMPersonAssignmentEvalDebug,
	LLMPersonAssignmentEvalDebug,
} from "../../api/llm/llm-person-assignment-eval";
import {
	getAdditionalPersonSkillsById,
	getPersonById,
	getPersonEducationById,
	getPersonExperiencesById,
	getPersonPhonenumberById,
	getPersonProfileSectionsById,
	getPersonSearchableLanguagesById,
	getPersonSkillsById,
	getPersonSummaryById,
	Person,
	PersonEducation as PersonEducationType,
	PersonExperienceType,
	PersonPhonenumber,
	PersonProfileSection,
	PersonSkill,
	PersonSummary,
	SearchableLanguage,
} from "../../api/person";
import type { PersonAttachment } from "../../api/person/person-attachment";
import {
	getPersonEmailsByPersonId,
	PersonEmail,
} from "../../api/personemail/person-email";
import {
	getTemplateContentForPerson,
	TemplateContentForPerson,
} from "../../api/templates/conversation-templates";

export type PersonDetails = {
	personSkills: PersonSkill[];
	additionalSkills: string[];
	languages: SearchableLanguage[];
	education: PersonEducationType[];
	profileSections: PersonProfileSection[];
	personEmails: PersonEmail[];
	experience: PersonExperienceType[];
	personPhonenumber: PersonPhonenumber[];
	personInfo: Person | null;
	personSummary: PersonSummary | null;
	jobPostings: LinkedinJobPosting[];
	templateContent: TemplateContentForPerson | null;
	attachments: PersonAttachment[];
	comments: Comment[];
	evalDebug: LLMPersonAssignmentEvalDebug | null;
};

// Parallel fetch person details — includes all data needed by grading view
export const fetchPersonDetails = async (
	personId: number,
	getLinkedinJobPostings: boolean = false,
	assignmentId?: number,
	showSummaryOnly: boolean = false,
	summaryWords: number = 500,
	includeContactInfo: boolean = true
): Promise<PersonDetails> => {
	const empty: PersonDetails = {
		personSkills: [],
		additionalSkills: [],
		languages: [],
		education: [],
		profileSections: [],
		personEmails: [],
		experience: [],
		personPhonenumber: [],
		personInfo: null,
		personSummary: null,
		jobPostings: [],
		templateContent: null,
		attachments: [],
		comments: [],
		evalDebug: null,
	};

	// If showSummaryOnly is true, ONLY fetch LLM summary (completely different endpoint)
	if (showSummaryOnly) {
		const summary = await getPersonSummaryById(personId, summaryWords);
		return { ...empty, personSummary: summary };
	}

	// Full fetch: get all person data in parallel
	const apiCalls: Record<string, () => Promise<unknown>> = {
		personSkills: () => getPersonSkillsById(personId, assignmentId),
		additionalSkills: () => getAdditionalPersonSkillsById(personId, assignmentId),
		languages: () => getPersonSearchableLanguagesById(personId),
		education: () => getPersonEducationById(personId),
		profileSections: () => getPersonProfileSectionsById(personId),
		experience: () => getPersonExperiencesById(personId, assignmentId),
		personInfo: () => getPersonById(personId, assignmentId),
	};

	// Only fetch contact info if it will be displayed
	if (includeContactInfo) {
		apiCalls["personPhonenumber"] = () => getPersonPhonenumberById(personId);
		apiCalls["personEmails"] = () => getPersonEmailsByPersonId(personId);
	}

	// Add jobPostings API call if needed
	if (getLinkedinJobPostings) {
		apiCalls["jobPostings"] = () => getLinkedinJobPostingsByPersonId(personId);
	}

	// Add template content API call if assignmentId is provided
	if (assignmentId) {
		apiCalls["templateContent"] = () =>
			getTemplateContentForPerson(assignmentId, personId);
		apiCalls["evalDebug"] = () =>
			getLLMPersonAssignmentEvalDebug(personId, assignmentId).catch(() => null);
	}

	// Execute all API calls in parallel
	const keys = Object.keys(apiCalls);
	const results = await Promise.all(keys.map((k) => apiCalls[k]()));

	// Map results back to personDetails
	const personDetails: PersonDetails = { ...empty };
	keys.forEach((key, index) => {
		const result = results[index];
		if (key === "additionalSkills") {
			// Extract the skills array from the DRF wrapper
			const wrapper = result as { results?: { additional_skills?: string[] }[] };
			personDetails.additionalSkills =
				wrapper.results?.[0]?.additional_skills ?? [];
		} else if (result && typeof result === "object" && "results" in result) {
			(personDetails as Record<string, unknown>)[key] = (
				result as { results: unknown }
			).results;
		} else {
			(personDetails as Record<string, unknown>)[key] = result;
		}
	});

	return personDetails;
};

// Custom use person details hook - if needed :)
// NOTE: additionalPersonSkills is NOT fetched here - it's loaded lazily in PersonSkills component
export const usePersonDetails = (personId: number) => {
	const [personSkills, setPersonSkills] = useState<PersonSkill[]>([]);
	const [languages, setLanguages] = useState<SearchableLanguage[]>([]);
	const [education, setEducation] = useState<PersonEducationType[]>([]);
	const [personEmails, setPersonEmails] = useState<PersonEmail[]>([]);
	const [experience, setExperience] = useState<PersonExperienceType[]>([]);
	const [personInfo, setPersonInfo] = useState<Person | null>(null);

	useEffect(() => {
		if (personId !== undefined && personId !== null && !Number.isNaN(personId)) {
			resetDetails();
			fetchPersonDetails(personId).then((personDetails) => {
				setPersonInfo(personDetails.personInfo);
				setPersonSkills(personDetails.personSkills);
				setLanguages(personDetails.languages);
				setEducation(personDetails.education);
				setPersonEmails(personDetails.personEmails);
				setExperience(personDetails.experience);
			});
		}
	}, [personId]);

	const resetDetails = () => {
		setPersonSkills([]);
		setLanguages([]);
		setEducation([]);
		setPersonEmails([]);
		setExperience([]);
		setPersonInfo(null);
	};

	return {
		personSkills,
		languages,
		education,
		personEmails,
		experience,
		personInfo,
	};
};
